import { getIntl } from '../../../../../utils/intl';
import { usePromptButton } from '../../../../shared/PromptButton';
import { Action } from '../../../../v2BaseComponents/ActionButtonLayout/types';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import useMedicalProfileFiles from '../../../../../hooks/api/useMedicalProfileFiles';
import { useEffect, useState } from 'react';
import { Input } from '../../../../v2BaseComponents/Inputs';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';

const intl = getIntl('EditFileButton', {
    promptHeader: 'Edit File',
    fileName: 'File Name',
    save: 'Save',
    cancel: 'Cancel',
});

export type UseEditFileActionArgs = {
    medicalProfileFileId: string;
    medicalProfileId: string;
};

export type UseEditFileActionReturnValue = {
    action: Action;
    Prompt: React.ReactNode;
};

export const useEditFileAction = (args: UseEditFileActionArgs): UseEditFileActionReturnValue => {
    const { file, isLoadingFile, updateFile, isUpdatingFile } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        medicalProfileFileId: args.medicalProfileFileId,
    });
    const canEdit = useHasPermission(args.medicalProfileId, 'files', 'edit');
    const [fileName, setFileName] = useState('');
    const currentFileName = file?.fileName || '';
    useEffect(() => {
        setFileName(currentFileName || '');
    }, [currentFileName]);
    const promptButton = usePromptButton({
        promptTitle: intl('promptHeader'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Input
                    title={intl('fileName')}
                    value={fileName}
                    onChange={(e) => {
                        setFileName(e.nativeEvent.text);
                    }}
                    testID="file-name-input"
                />
                <PromptActions
                    actions={[
                        {
                            title: intl('save'),
                            isDisabled: isUpdatingFile || !fileName,
                            onPress: () => {
                                updateFile({ medicalProfileFileId: args.medicalProfileFileId, fileName });
                                onClose();
                            },
                            testID: 'edit-file-confirm-button',
                            icon: 'CheckCircle',
                        },
                        {
                            title: intl('cancel'),
                            isDisabled: isUpdatingFile,
                            onPress: () => {
                                onClose();
                            },
                            testID: 'edit-file-cancel-button',
                            icon: 'XCircle',
                            variant: 'secondary',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    return {
        action: {
            title: intl('promptHeader'),
            icon: 'Edit2',
            onPress: promptButton.onPress,
            show: canEdit && !!file && file.sourceEntityType === 'file',
            testID: 'edit-file-button',
            isDisabled: isLoadingFile,
        },
        Prompt: promptButton.Prompt,
    };
};
